/**
 * Template Library — Epic 1 AC #1, #2, #4.
 *
 * Groups templates by type (Manifest, FedEx, UPS, DHL).
 * Row actions: Edit (→ Builder), Activate, Deactivate, Duplicate.
 * Deactivation guard: deactivate is disabled for the last Active template of
 * its type when there are pending uploads (guard enforced server-side; UI
 * shows the button but handles the 409 error).
 */

import {
  IconCheck,
  IconCopy,
  IconPencil,
  IconPlus,
  IconPower,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";

import type { TemplateOut, TemplateType } from "./types";
import { useTemplateApi } from "./api";

// ── Constants ─────────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<TemplateType, string> = {
  manifest: "Manifest",
  fedex: "FedEx",
  ups: "UPS",
  dhl: "DHL",
};

const TYPE_ORDER: TemplateType[] = ["manifest", "fedex", "ups", "dhl"];

const SURCHARGE_FORMAT_LABELS: Record<string, string> = {
  row_per_surcharge: "Row per surcharge",
  column_per_surcharge: "Column per surcharge",
};

// ── StatusBadge ───────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const isActive = status === "Active";
  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded px-2 py-0.5 font-mono text-[11px] font-medium",
        isActive
          ? "bg-status-match-bg text-status-match"
          : "bg-bg-4 text-text-2",
      ].join(" ")}
      aria-label={`Status: ${status}`}
    >
      {isActive && <IconCheck size={10} aria-hidden />}
      {status}
    </span>
  );
}

// ── TemplateRow ───────────────────────────────────────────────────────────────

interface TemplateRowProps {
  template: TemplateOut;
  onActivate: (id: string) => void;
  onDeactivate: (id: string) => void;
  onDuplicate: (id: string) => void;
  isMutating: boolean;
}

function TemplateRow({
  template: t,
  onActivate,
  onDeactivate,
  onDuplicate,
  isMutating,
}: TemplateRowProps) {
  const isActive = t.status === "Active";

  return (
    <tr className="border-b border-border last:border-0 hover:bg-bg-3 transition-colors">
      <td className="px-4 py-3 text-sm text-text font-medium">{t.name}</td>
      <td className="px-4 py-3">
        <StatusBadge status={t.status} />
      </td>
      <td className="px-4 py-3 text-xs text-text-2">
        {t.surcharge_format
          ? SURCHARGE_FORMAT_LABELS[t.surcharge_format] ?? t.surcharge_format
          : "—"}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-text-3">v{t.version}</td>
      <td className="px-4 py-3 text-xs text-text-3">{t.fields.length} fields</td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 justify-end">
          <Link
            to="/templates/$templateId/edit"
            params={{ templateId: t.id }}
            aria-label={`Edit ${t.name}`}
            className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-text-2 hover:text-text hover:bg-bg-3 transition-colors"
          >
            <IconPencil size={12} aria-hidden />
            Edit
          </Link>

          {isActive ? (
            <button
              type="button"
              aria-label={`Deactivate ${t.name}`}
              disabled={isMutating}
              className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-text-2 hover:text-text hover:bg-bg-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={() => onDeactivate(t.id)}
            >
              <IconPower size={12} aria-hidden />
              Deactivate
            </button>
          ) : (
            <button
              type="button"
              aria-label={`Activate ${t.name}`}
              disabled={isMutating}
              className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-status-match hover:bg-bg-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              onClick={() => onActivate(t.id)}
            >
              <IconCheck size={12} aria-hidden />
              Activate
            </button>
          )}

          <button
            type="button"
            aria-label={`Duplicate ${t.name}`}
            disabled={isMutating}
            className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs text-text-2 hover:text-text hover:bg-bg-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => onDuplicate(t.id)}
          >
            <IconCopy size={12} aria-hidden />
            Duplicate
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── TypeSection ───────────────────────────────────────────────────────────────

interface TypeSectionProps {
  type: TemplateType;
  templates: TemplateOut[];
  onActivate: (id: string) => void;
  onDeactivate: (id: string) => void;
  onDuplicate: (id: string) => void;
  isMutating: boolean;
}

function TypeSection({
  type,
  templates,
  onActivate,
  onDeactivate,
  onDuplicate,
  isMutating,
}: TypeSectionProps) {
  if (templates.length === 0) return null;

  return (
    <section aria-labelledby={`type-${type}`} className="mb-6">
      <h2
        id={`type-${type}`}
        className="mb-2 text-[11px] font-medium uppercase tracking-widest text-text-3"
      >
        {TYPE_LABELS[type]}
      </h2>
      <div className="rounded-lg border border-border bg-bg-2 overflow-hidden">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-bg-3 text-left">
              <th className="px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-text-3">
                Name
              </th>
              <th className="px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-text-3">
                Status
              </th>
              <th className="px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-text-3">
                Surcharge format
              </th>
              <th className="px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-text-3">
                Version
              </th>
              <th className="px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-text-3">
                Fields
              </th>
              <th className="px-4 py-2" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {templates.map((t) => (
              <TemplateRow
                key={t.id}
                template={t}
                onActivate={onActivate}
                onDeactivate={onDeactivate}
                onDuplicate={onDuplicate}
                isMutating={isMutating}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ── TemplateLibraryPage ───────────────────────────────────────────────────────

export function TemplateLibraryPage() {
  const api = useTemplateApi();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: templates = [], isLoading, error } = useQuery({
    queryKey: ["templates"],
    queryFn: () => api.listTemplates(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["templates"] });

  const activateMutation = useMutation({
    mutationFn: (id: string) => api.activateTemplate(id),
    onSuccess: invalidate,
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.deactivateTemplate(id),
    onSuccess: invalidate,
    onError: (err) => {
      // 409 = deactivation guard — surface the server message.
      alert(err instanceof Error ? err.message : "Deactivation failed.");
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => api.duplicateTemplate(id),
    onSuccess: (tmpl) => {
      invalidate();
      void navigate({ to: "/templates/$templateId/edit", params: { templateId: tmpl.id } });
    },
  });

  const isMutating =
    activateMutation.isPending ||
    deactivateMutation.isPending ||
    duplicateMutation.isPending;

  const byType = TYPE_ORDER.reduce<Record<TemplateType, TemplateOut[]>>(
    (acc, t) => {
      acc[t] = templates.filter((tmpl) => tmpl.template_type === t);
      return acc;
    },
    { manifest: [], fedex: [], ups: [], dhl: [] },
  );

  const hasAny = templates.length > 0;

  return (
    <div className="max-w-5xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold text-text">Templates</h1>
          <p className="mt-0.5 text-sm text-text-2">
            Manage header mappings for manifest and carrier invoice files.
          </p>
        </div>
        <Link
          to="/templates/new"
          className="flex items-center gap-2 rounded bg-accent px-3 py-2 text-sm font-medium text-bg hover:opacity-90 transition-opacity"
          aria-label="New template"
        >
          <IconPlus size={14} aria-hidden />
          New template
        </Link>
      </div>

      {isLoading && (
        <p className="text-sm text-text-2">Loading templates…</p>
      )}

      {error && (
        <div role="alert" className="alert error">
          <span>Failed to load templates: {(error as Error).message}</span>
        </div>
      )}

      {!isLoading && !error && !hasAny && (
        <div className="rounded-lg border border-border bg-bg-2 p-10 text-center">
          <p className="text-sm text-text-2">No templates yet.</p>
          <p className="mt-1 text-xs text-text-3">
            Create a template to enable file ingestion.
          </p>
          <Link
            to="/templates/new"
            className="mt-4 inline-flex items-center gap-2 rounded bg-accent px-3 py-2 text-sm font-medium text-bg hover:opacity-90 transition-opacity"
          >
            <IconPlus size={14} aria-hidden />
            Create first template
          </Link>
        </div>
      )}

      {!isLoading &&
        !error &&
        hasAny &&
        TYPE_ORDER.map((type) => (
          <TypeSection
            key={type}
            type={type}
            templates={byType[type]}
            onActivate={(id) => activateMutation.mutate(id)}
            onDeactivate={(id) => deactivateMutation.mutate(id)}
            onDuplicate={(id) => duplicateMutation.mutate(id)}
            isMutating={isMutating}
          />
        ))}
    </div>
  );
}
